import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ACCOUNT_INACTIVE_BROKER_MESSAGE, CAPTCHA_FAILED, ConfirmationDialogService, ERROR_CALLING_RPC, ERROR_LOGIN_WITH_GOOGLE, EventsService, LIMIT_BREACHED_LOGOUT_MESSAGE, LOGGED_IN_MSG, NgxToastrService, USER_LOCKED } from '@app/_shared';
import { InterceptorErrorType } from '@app/_shared/shared.enums';
import { PostSILoginRedirect } from '@app/app.structs';
import { AccountState } from '@app/modules/admin/providers/user-management/user-management.enums';
import { LoginSource } from '@app/modules/trading/modules/profile/components/personal-details/interfaces/personal-details.enum';
import { ConfigurationService } from '@config/configuration.service';
import * as moment from 'moment';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, share, shareReplay, tap } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';
import { COOKIE_NAMES, CustomEvents, EPOCH_EXPIRES, LOCAL_STORAGE_KEYS, SELECTED_VENDOR, SUPERADMIN_LOGIN_PATH, StorageHelper, Utils, isOperator } from '../../../../_helpers';
import { ShareIndiaRedirectResponseParams } from '../../components/social-callback/social-callback.component';
import { WebErrorCodes } from './../../../../../utProto/webEnums_pb';
import { crmUtmFieldsArray, FormType, OTPType, VENDOR_SOURCE_ALLOWED } from './auth.constants';
import { APPLE_VENDOR_NAME, GOOGLE_VENDOR_NAME } from '../social-auth/social-auth.constants';
import {
    ChangePasswordRequest,
    GenericResponse,
    LoginRequest, LoginResponse,
    MPINReqest,
    MastertrustLoginRequest,
    MultiBrokerEmailPhoneLoginRequest,
    MultiBrokerRefreshTokenResponse,
    MultiBrokerSocialLoginInitResponse,
    MultiBrokerVerifyOTPRequest,
    MultiBrokerVerifyOTPResponse,
    OperatorLoginRequest,
    PasswordPolicyInterface,
    PhoneLoginRequest,
    PhoneLoginResponse,
    ReffererStructure,
    RefreshPasswordResponse,
    SignUpRequest,
    SocialLoginInitResponse,
    SwastikaLoginRequest,
    TwoFAEnabledResponse,
    UserInfo,
    ValidateOTPResponse,
    VerifyEmailRequest,
    VerifyPhoneRequest
} from './auth.interfaces';
import { AuthRole, SocialLoginProvider, TwoFATypes } from './auth.structs';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ActiveSessionLimitReachedComponent } from '@app/_shared/modules/active-session-limit-reached/active-session-limit-reached.component';
import { ActiveSession } from '@app/modules/trading/modules/profile/components/personal-details/interfaces/personal-details.interfaces';
import { CacheIdbService } from '@app/_shared/providers/cache-idb/cache-idb.service';
import { MixpanelService } from '@app/_shared/providers/mixpanel/mixpanel.service';
import { MIXPANEL_EVENTS } from '@app/_shared/providers/mixpanel/mixpanel.events';
import { BrokerConnectionStatus } from '../../../../../utProto/enums_pb';
import { Vendor } from '@app/modules/trading/modules/manage-brokers/manage-broker.interface';

class JWTUtilClass {
    IP: string;
    clientId: string;
    exp: number;
    role: AuthRole; // To be added
    loginSource: LoginSource;
    creationTime: number; // token creation time at server level
    sessionID: string;
    delta: number;

    constructor(
        token: string,
        isServerFetch?: boolean
    ) {
        const claims = JSON.parse(atob(token.split('.')[1]));
        this.IP = claims.ip_address;
        this.clientId = claims.clientId;
        this.exp = claims.exp;
        if (claims.role) {
            this.role = claims.role;
        }
        this.loginSource = claims.login_source;
        this.creationTime = claims.iat;
        this.sessionID = claims.session_id;

        /**
         * getting the difference between current system time and
         * token creation time and storing in delta
         */
        if (isServerFetch) {
            StorageHelper.deltaValue = moment().unix() - this.creationTime;
        }
        this.delta = StorageHelper.deltaValue;

        this.setSessionExpiryCookieFromAccessToken(claims as any);
    }

    get isExpired() {
        if (!this.exp) { return true }
        // Token is refreshed 25 seconds prior to actual expiry time.
        // We will not wait for exact expiry.
        // Done for handling cases in which there might be some time difference
        // different modules and case in which a request is sent just before
        // actual expiry time.
        //  Done for RA-103, RA-121.
        return ((this.exp + this.delta) - moment().unix()) <= 25;
    }


    /**
     * Sets the browser cookie "ut_session_exp" to the JWT "exp" value, 
     * and sets cookie expiry to the token "exp" (seconds since epoch, UTC).
     * @param claims expects JWT decoded payload with "exp" as expiry
     */

    setSessionExpiryCookieFromAccessToken(claims: any) {
        if (!claims || !claims.exp) return;
        // exp in JWT are in seconds, Date wants milliseconds for expiry
        const expiryEpochUTC = claims.exp; // seconds
        const expiryDate = new Date(expiryEpochUTC * 1000); // milliseconds

        const cookieValue = expiryEpochUTC.toString(); // store iat as string
        // Set cookie string
        // Expires must be in UTC string format per RFC
        document.cookie = `${COOKIE_NAMES.SESSION_EXPIRY}=${cookieValue}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax;`;
    }
}

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    accessTokenObj: JWTUtilClass;
    refreshTokenObj: JWTUtilClass;
    userInfo: UserInfo;
    passwordPolicyCache: PasswordPolicyInterface;
    private passwordPolicy$: Observable<PasswordPolicyInterface>;
    forgotEmail: string = '';
    isLogoutRequestSent: boolean = false;
    captchaToken: string = 'abcdefg';
    socialLoginLink: string = '';
    isPlanAlert: boolean = false;
    isNewFeatureAlert: boolean = false;
    refreshToken$: Observable<MultiBrokerRefreshTokenResponse> | null = null;
    // Shared login/sign-up flow state — used by AuthComponent (step indicator, sign-up CTA) and MultiBrokerLoginComponent
    isLoginPage: boolean = true; // true = existing user login, false = new account sign-up
    formType: FormType = FormType.LOGIN;
    selectBrokerDetails: Vendor | null = null;

    constructor(
        private http: HttpClient,
        private configurationService: ConfigurationService,
        private router: Router,
        private eventsService: EventsService,
        private toastr: NgxToastrService,
        private confirmationService: ConfirmationDialogService,
        private dialog: MatDialog,
        private cacheIdbService: CacheIdbService,
        private snackbar: NgxToastrService,
        private mixpanel: MixpanelService
    ) {
        this.setToken();
    }

    /** Switches between login and sign-up mode on the shared email/phone form. */
    toggleLoginPage() {
        this.isLoginPage = !this.isLoginPage;
    }

    setToken() {
        /**
         * if there is any issue while fetcing userinfo from
         * localstorage then clearing localstorage and reloading web
         */
        try {
            this.userInfo = StorageHelper.userInfo;
            if (this.userInfo) {
                this.accessTokenObj = new JWTUtilClass(this.userInfo.access);
                this.refreshTokenObj = new JWTUtilClass(this.userInfo.refresh);
                this.syncMixpanelIdentity();
            }
        }
        catch {
            this.clearLogout();
        }
    }

    /**
     * Single place to bind Mixpanel distinct_id to the logged-in client.
     * Called whenever JWT session is loaded or refreshed (not on anonymous/login-only pages).
     */
    private syncMixpanelIdentity(): void {
        const clientId = this.accessTokenObj?.clientId;
        if (!clientId) return;

        this.mixpanel.identify(clientId);
        this.mixpanel.setUserProperties({
            loginSource: this.accessTokenObj.loginSource
        });
    }


    loginSwastika(req: SwastikaLoginRequest) {
        const headers = Utils.getRequestHeadersSwastika().append('X-Recaptcha-Token', this.captchaToken);
        if (StorageHelper.userInfo?.access) {
            this.toastr.simpleCustomToaster(LOGGED_IN_MSG);
            this.setToken();
            this.router.navigate(['/trading/dashboard']);
        } else {
            const uGateway = this.configurationService.applicationUrls.uGateWayLogin;
            return this.http.post(uGateway, req, {
                headers
            })
        }
    }

    loginMastertrust(req: MastertrustLoginRequest) {
        const headers = Utils.getRequestHeadersMastertrust().append('X-Recaptcha-Token', this.captchaToken);

        if (StorageHelper.userInfo?.access) {
            this.toastr.simpleCustomToaster(LOGGED_IN_MSG);
            this.setToken();
            this.router.navigate(['/trading/dashboard']);
        } else {
            const uGateway = this.configurationService.applicationUrls.mastertrustLogin;
            return this.http.post(uGateway, req, {
                headers
            })
        }
    }

    phoneLogin(req: PhoneLoginRequest) {
        const headers = new HttpHeaders({
            'X-Recaptcha-Token': this.captchaToken
        })

        const url = this.configurationService.applicationUrls.phoneLogin;
        return this.http.post(url, req, {
            headers
        });

    }

    verifyAdminLogin(req: LoginRequest) {
        const headers = new HttpHeaders({
            'X-Recaptcha-Token': this.captchaToken
        })
        return this.http.post<LoginResponse>(this.configurationService.applicationUrls.login, req, { headers });
    }


    async login(req: LoginRequest, notAuthenticityCheck = true, affiliate = false) {
        const headers = new HttpHeaders({
            'X-Recaptcha-Token': this.captchaToken
        })

        if (notAuthenticityCheck && StorageHelper.userInfo?.access) {
            this.toastr.simpleCustomToaster(LOGGED_IN_MSG);
            this.setToken();
            this.router.navigate(['/trading/dashboard']);
        } else {
            const url = affiliate ? this.configurationService.applicationUrls.affiliateHostLogin : this.configurationService.applicationUrls.login;
            return this.http.post<LoginResponse | TwoFAEnabledResponse>(url, req, {
                headers
            })
                .pipe(
                    tap(resp => {
                        if (!('twoFA' in resp)) {
                            StorageHelper.TwoFAToken = null;
                            if (resp.password_state !== AccountState.FirstTime) {
                                this.handleAuthResponse(resp);
                            }
                        } else {
                            if (
                                (resp.twoFA_type === TwoFATypes.MPIN) &&
                                !resp.reset_mpin &&
                                resp.password_state !== AccountState.FirstTime
                            ) {
                                StorageHelper.TwoFAToken = resp.twoFA_token;
                            }
                        }
                    }),
                    catchError(err => {
                        this.handleUnVerifiedError(err.error);
                        if (err.error?.errorCode === WebErrorCodes.WEBERRORCODES_USERLOCKED) {
                            this.handleUserLockedError();
                        }
                        return throwError(err);
                    })
                );
        }
    }

    loginOperator(req: OperatorLoginRequest) {
        if (!this.captchaToken) {
            this.snackbar.simpleCustomToaster(CAPTCHA_FAILED);
            return;
        }
        const headers = new HttpHeaders({
            'X-Recaptcha-Token': this.captchaToken
        })
        return this.http.post<any>(this.configurationService.applicationUrls.loginOperator, req, {
            headers
        })
    }

    refreshToken() {
        if (this.refreshToken$) {
            return this.refreshToken$;
        }
        this.refreshToken$ = this.http.post<MultiBrokerRefreshTokenResponse>(this.configurationService.applicationUrls.multiBrokerRefreshToken, {
            refresh_token: this.userInfo.refresh
        })
            .pipe(
                share(),
                tap(res => {
                    this.refreshToken$ = null;
                    this.userInfo.access = res.access_token;
                    this.userInfo.refresh = res.refresh_token;
                    this.accessTokenObj = new JWTUtilClass(this.userInfo.access, true);
                    this.refreshTokenObj = new JWTUtilClass(this.userInfo.refresh, true);
                    if (!StorageHelper.TwoFAToken) {
                        StorageHelper.userInfo = this.userInfo;
                    }
                    this.syncMixpanelIdentity();
                }),
                finalize(() => { this.refreshToken$ = null; }),
                shareReplay({ bufferSize: 1, refCount: true })
            );
        return this.refreshToken$;
    }

    validateUserForBrokerLogin(broker: string, requestToken: string) {
        return this.http.get<any>(this.configurationService.applicationUrls.loginBroker + broker + '/validate-oauth-errors/', {
            headers: {
                'request-token': requestToken
            }
        });
    }

    handleAuthResponse(resp: LoginResponse | PhoneLoginResponse, postSiRedirectUrl?: PostSILoginRedirect) {
        const id = uuid();
        StorageHelper.Uuid = id;

        this.userInfo = {
            access: resp.access,
            refresh: resp.refresh
        };

        this.accessTokenObj = new JWTUtilClass(this.userInfo.access, true);
        this.refreshTokenObj = new JWTUtilClass(this.userInfo.refresh, true);
        StorageHelper.userInfo = this.userInfo;
        this.syncMixpanelIdentity();
        resp.isFirstLogin && (StorageHelper.isFirstLogin = resp.isFirstLogin);
        let url = '';
        if ('signup' in resp) {
            url = `/trading/dashboard/?signup=${resp.signup}`;
        }
        else if (postSiRedirectUrl === PostSILoginRedirect.plan) {
            StorageHelper.clearByKey(LOCAL_STORAGE_KEYS.POST_SI_REDIRECT_URL)
            url = '/trading/profile/user/plans';
        }
        else if (postSiRedirectUrl === PostSILoginRedirect.funds) {
            StorageHelper.clearByKey(LOCAL_STORAGE_KEYS.POST_SI_REDIRECT_URL)
            url = '/trading/profile/funds';
        }
        else {
            url = '/trading/dashboard';
        }
        window.location.replace(window.location.origin + url)
    }

    get isAuthenticated() {
        return !!(this.userInfo);
    }

    // We want vendor behaivour in case of StratX as well
    get isVendorLogin() {
        return (this.accessTokenObj.loginSource === VENDOR_SOURCE_ALLOWED[SELECTED_VENDOR.loginSource]) || (SELECTED_VENDOR.loginSource.toLowerCase() === LoginSource.STRATX.toLowerCase());
    }

    get isSwastikaLogin() {
        return this.accessTokenObj.loginSource === LoginSource.SWASTIKA;
    }

    get isShareIndiaLogin() {
        return this.accessTokenObj.loginSource === LoginSource.SHARE_INDIA;
    }

    get isMasterTrustLogin() {
        return this.accessTokenObj.loginSource === LoginSource.MASTERTRUST;
    }

    get isPesbLogin() {
        return this.accessTokenObj.loginSource === LoginSource.PESB;
    }

    get isAdminLogin() {
        return this.accessTokenObj?.role === AuthRole.ADMIN;
    }

    // check if session is logged in in case of same platform Type
    isSessionLoggedIn(phone: string) {
        return this.http.get<{ is_session_active: boolean }>(this.configurationService.applicationUrls.isSessionLoggedIn + phone);
    }

    async initSocialLogin(provider: SocialLoginProvider, connectToBroker = false, postSiRedirectUrl?: PostSILoginRedirect) {
        if (!connectToBroker && StorageHelper.userInfo?.access) {
            this.toastr.simpleCustomToaster(LOGGED_IN_MSG);
            this.setToken();
            this.router.navigate(['/trading/dashboard']);
        } else {
            const headers = new HttpHeaders({
                'X-Recaptcha-Token': this.captchaToken
            });
            postSiRedirectUrl && (StorageHelper.postSiRedirectUrl = postSiRedirectUrl);
            return this.http.get<SocialLoginInitResponse>(this.configurationService.applicationUrls.socialLoginInit + provider + '/', {
                headers: headers
            });
        }
    }

    async socialLoginCallback(provider: SocialLoginProvider, params: any, flag: boolean = true) {
        // In case of google social callback
        const headers = new HttpHeaders({
            'X-Recaptcha-Token': this.captchaToken
        })

        try {

            // getting consent token and checking if active sessions are present then showing dialog else proceeding with social login.
            const consentLoginResponse = await this.http.post<LoginResponse>(this.configurationService.applicationUrls.getMultiBrokerSocialLoginCallback(provider), params, {
                headers: headers
            }).toPromise();

            /*
                if consent token is not present then we are getting token from response and returning the token as observale
                else we are showing dialog for active sessions or proceeding with social login.
             */
            if (!consentLoginResponse.consentToken) {
                // this.handleAuthResponse(consentLoginResponse, StorageHelper.postSiRedirectUrl);
                this.handleMultiBrokerAuthResponse(consentLoginResponse)
                return of(consentLoginResponse);
            } else {
                if (consentLoginResponse.activeSessions?.length && flag) {
                    consentLoginResponse.activeSessions[0].login_date = +consentLoginResponse.activeSessions[0].login_date * 1000;
                    return await this.openLimitBreachedDialog(null, consentLoginResponse.activeSessions[0], consentLoginResponse.consentToken, true);
                }
                else {
                    return this.proceedSocialLoginAfterConsent(consentLoginResponse.consentToken);
                }
            }

        } catch (error) {
            console.error(error);
            this.captchaToken = '';
            return throwError(error);
        }

    }

    async verifyMPIN(request: MPINReqest) {
        const headers = new HttpHeaders({
            'X-Recaptcha-Token': this.captchaToken
        })
        return this.http.post<LoginResponse>(this.configurationService.applicationUrls.verifyMPIN, request, {
            headers
        })
            .pipe(
                tap(resp => {
                    if (resp.password_state === AccountState.FirstTime) {
                        return;
                    }
                    this.handleAuthResponse(resp);
                })
            );
    }

    setMpin(request: MPINReqest) {
        return this.http.post<GenericResponse>(this.configurationService.applicationUrls.setMPIN, request);
    }

    forgotMpin(email: string) {
        return this.http.post<GenericResponse>(this.configurationService.applicationUrls.forgotMpin, { email });
    }

    async signUp(request: SignUpRequest) {
        const headers = new HttpHeaders({
            'X-Recaptcha-Token': this.captchaToken
        })
        return this.http.post<GenericResponse>(this.configurationService.applicationUrls.signUpUrl, request, {
            headers
        }).pipe(
            catchError(err => {
                this.handleErrors(err.error);
                return throwError(err);
            })
        )
    }

    validateUsername(username: string, broker: string) {
        return this.http.get<any>(this.configurationService.applicationUrls.validateUsername + '/' + broker + '/exists/' + username);
    }

    handleErrors(err) {
        if (err?.error_type === InterceptorErrorType.EMAILALREADYREGISTERED) {
            this.eventsService.publish(CustomEvents.EmailMobileAlreadyVerified, err?.error_type)
        }
        else if (err?.error_type === InterceptorErrorType.PHONENOALREADYREGISTERED) {
            this.eventsService.publish(CustomEvents.EmailMobileAlreadyVerified, err?.error_type)
        }
        else {
            this.handleUnVerifiedError(err);
        }
    }

    finishSignUp(request: VerifyEmailRequest) {
        return this.http.post<GenericResponse>(this.configurationService.applicationUrls.verifyEmail, request);
    }

    validatePhoneOTP(otp: string, phone: string) {
        return this.http.post<ValidateOTPResponse>(this.configurationService.applicationUrls.validateMobileOTP, {
            phone, otp
        });
    }

    async finishPhoneSignIn(request: VerifyPhoneRequest, response: ValidateOTPResponse) {
        // popup to be added
        if (response.activeSessions?.length) {
            response.activeSessions[0].login_date = +response.activeSessions[0].login_date * 1000;
            return await this.openLimitBreachedDialog(request, response.activeSessions[0], response.consentToken);
        }
        else {
            return this.sentFinishPhoneSignIn(request, response.consentToken);
        }


    }

    sentFinishPhoneSignIn(request: VerifyPhoneRequest, consentToken: string) {
        return this.http.post<PhoneLoginResponse>(this.configurationService.applicationUrls.verifyPhone, request, {
            headers: {
                'authorization-consent': consentToken
            }
        }).pipe(
            tap(resp => {
                StorageHelper.TwoFAToken = null;
                this.handleAuthResponse(resp);
            }),
            catchError(err => {
                this.handleUnVerifiedError(err.error);
                if (err.error?.errorCode === WebErrorCodes.WEBERRORCODES_USERLOCKED) {
                    this.handleUserLockedError();
                }
                return throwError(err);
            })
        );
    }

    proceedSocialLoginAfterConsent(consentToken: string) {
        const headers = new HttpHeaders({
            'authorization-consent': consentToken,
            'X-Recaptcha-Token': this.captchaToken
        })
        return this.http.get<LoginResponse>(this.configurationService.applicationUrls.consentToken, { headers }).pipe(
            tap(resp => {
                this.handleAuthResponse(resp, StorageHelper.postSiRedirectUrl);
            }),
            catchError(err => {
                console.error(err);
                return throwError(err);
            })
        );
    }

    /**
     * Opens a dialog for the limit breached scenario.
     * 
     * @param request - The VerifyPhoneRequest object.
     * @param activeSession - The ActiveSession object.
     * @param consentToken - The consent token string.
     * @param isSocialLogin - Optional flag indicating if it is a social login.
     * @returns A Promise that resolves to the result of the dialog.
     */
    async openLimitBreachedDialog(request: VerifyPhoneRequest, activeSession: ActiveSession, consentToken: string, isSocialLogin = false) {
        const dialogRef = this.dialog.open(ActiveSessionLimitReachedComponent, {
            disableClose: true,
            width: '400px',
            height: '420px',
            backdropClass: "backdrop-blur",
            panelClass: ['email-communicaion-dialog'],
            autoFocus: false,
            data: {
                activeSession
            }
        })

        const res = await dialogRef.afterClosed().toPromise()
        if (res) {
            if (isSocialLogin) {
                return this.proceedSocialLoginAfterConsent(consentToken);
            } else {
                return this.sentFinishPhoneSignIn(request, consentToken);
            }
        } else {
            this.deleteConsentToken(consentToken).subscribe();
            return of(null);
        }
    }

    deleteConsentToken(consentToken: string) {
        return this.http.delete(this.configurationService.applicationUrls.consentToken, {
            headers: {
                'authorization-consent': consentToken
            }
        });
    }

    validateOTP(otpType: OTPType, otp: string, email: string) {
        return this.http.post<ValidateOTPResponse>(this.configurationService.applicationUrls.validateOTP + otpType, {
            email, otp
        });
    }

    handleUnVerifiedError(err) {
        if (err?.error_type === InterceptorErrorType.EMAILNOTVERIFIED && err.email) {
            this.eventsService.publish(CustomEvents.ReVerifyEmail, [err?.email, err?.error_type]);
        }
    }

    handleMaxResendAttemptsError(err) {
        if (err?.error_type === InterceptorErrorType.MAXRESENDOTPATTEMPTS && err.email) {
            this.eventsService.publish(CustomEvents.RouteToSignup, err.email);
        }
    }

    handleUserLockedError() {
        this.confirmationService.alert({
            message: USER_LOCKED,
            buttons: [
                {
                    text: 'Cancel',
                    accent: true,
                    class: 'undeploy-button',
                },
                {
                    text: 'Proceed',
                    accent: true,
                    class: 'new-theme-gradient-bg',
                    handler: () => {
                        this.router.navigate(['/auth/forgot']);
                    }
                }]
        })
    }

    async forgotPassword(email: string, isAffiliate = false) {
        const headers = new HttpHeaders({
            'X-Recaptcha-Token': this.captchaToken
        })
        const url = isAffiliate ? this.configurationService.applicationUrls.forgotPasswordAffiliate : this.configurationService.applicationUrls.resetPassword
        return this.http.post<GenericResponse>(url, {
            email
        }, {
            headers
        }).pipe(
            catchError(err => {
                this.handleUnVerifiedError(err.error);
                return throwError(err);
            })
        )
    }

    async verifyOTP(token: string, otp: string) {
        const headers = new HttpHeaders({
            'X-Recaptcha-Token': this.captchaToken
        })

        return this.http.post<LoginResponse | GenericResponse>(this.configurationService.applicationUrls.otp, {
            otp,
            twoFaToken: token
        }, {
            headers
        }).pipe(
            tap(res => {
                if ('access' in res) {
                    if (res.password_state === AccountState.FirstTime) {
                        return;
                    }
                    this.handleAuthResponse(res);
                }
            })
        );
    }

    resendOTP(token: string) {
        return this.http.put<TwoFAEnabledResponse>(this.configurationService.applicationUrls.otp, {
            twoFaToken: token
        });
    }

    sendEmailOTP(phone: string, email: string) {
        return this.http.post<GenericResponse>(this.configurationService.applicationUrls.sendEmailOtp + email, {
            phone: phone
        });
    }

    resendEmailOtp(userEmail: string, email = true, phone = false) {
        const headers = new HttpHeaders({
            'X-Recaptcha-Token': this.captchaToken
        })
        return this.http.post<GenericResponse>(this.configurationService.applicationUrls.resendEmailOtp + userEmail, {
            params: { email, phone } as any,
            headers: headers
        }).pipe(
            catchError(err => {
                this.handleMaxResendAttemptsError(err.error);
                return throwError(err);
            }));
    }

    verifyEmailOtp(email: string, otp: string, existingEmail: boolean = false) {
        return this.http.post<any>(this.configurationService.applicationUrls.verifyEmailOtp, {
            email: email,
            otp: otp,
            otpType: OTPType.EmailOTPType,
            existingEmail: existingEmail
        });
    }

    async changePassword(request: ChangePasswordRequest) {
        const headers = new HttpHeaders({
            'X-Recaptcha-Token': this.captchaToken
        })
        return this.http.post<GenericResponse>(this.configurationService.applicationUrls.changePassword, request, {
            headers
        });
    }

    logoutApi(socialLoginLink?: string) {
        // If multiple logout request received then only send one request
        if (!this.isLogoutRequestSent) {
            this.isLogoutRequestSent = true
            if (this.isAdminLogin) {
                this.clearLogout();
            } else {
                this.http.delete(this.configurationService.applicationUrls.userLogout).subscribe(() => {
                    this.isLogoutRequestSent = false;
                    this.clearLogout(socialLoginLink);
                });
            }
        }
    }

    cancelConnectBroker(provider: SocialLoginProvider, code: string) {
        const url = this.configurationService.applicationUrls.cancelConnectBroker + provider + '/';
        return this.http.post(url, {
            code
        });
    }

    checkLastSession() {
        return this.http.get<boolean>(this.configurationService.applicationUrls.lastSession);
    }

    clearLogout(locationPath: string = null) {
        // Save LAST_LOGGED_IN_USER in memory before clearing storage
        const lastLoggedInUser = StorageHelper.lastLoggedInUser;

        StorageHelper.clearAll();
        this.mixpanel.reset();
        this.cacheIdbService.clearCache();
        // Clear cookies for ut_session_exp
        if (typeof document !== 'undefined') {
            document.cookie = `${COOKIE_NAMES.SESSION_EXPIRY}=; Path=/; Expires=${EPOCH_EXPIRES};`;
        }

        // Restore LAST_LOGGED_IN_USER before reload
        if (lastLoggedInUser) {
            StorageHelper.lastLoggedInUser = lastLoggedInUser;
        }

        locationPath ? (window.location.href = locationPath) : location.reload();
    }

    /**
     * Password Policy is being cached using shareReplay to prevent duplicate HTTP calls.
     * This ensures that even if called multiple times simultaneously, only one HTTP request is made.
     */
    getPasswordPolicy(): Observable<PasswordPolicyInterface> {
        if (!this.passwordPolicy$) {
            this.passwordPolicy$ = this.http.get<PasswordPolicyInterface>(
                this.configurationService.applicationUrls.passwordPolicy
            ).pipe(
                tap((response) => {
                    this.passwordPolicyCache = response;
                }),
                shareReplay(1) // Cache the result and share it with all subscribers
            );
        }
        return this.passwordPolicy$;
    }
    // TODO: template handling
    async resendVerifyEmailLink(userEmail: string, email = true, mobile = true) {
        const headers = new HttpHeaders({
            'X-Recaptcha-Token': this.captchaToken
        })
        return this.http.get<any>(this.configurationService.applicationUrls.resendLink + userEmail, {
            params: { email, mobile } as any,
            headers: headers
        })
            .pipe(
                catchError(err => {
                    this.handleMaxResendAttemptsError(err.error);
                    return throwError(err);
                }));
    }

    logoutSession(session_id: string) {
        return this.http.post(this.configurationService.applicationUrls.logoutSessions, null, {
            params: {
                sessionID: session_id
            } as any
        })
    }

    logoutAll() {
        return this.http.post(this.configurationService.applicationUrls.logoutSessions, null, {
            params: {
                logoutAll: 1
            } as any
        })
    }

    logoutSessionById(session_id: string) {
        return this.http.delete(this.configurationService.applicationUrls.userLogout + '/' + session_id);
    }

    logoutAllSessions() {
        return this.http.delete(this.configurationService.applicationUrls.userLogout + '/all');
    }

    getSocialMapping(req: ShareIndiaRedirectResponseParams) {
        const url = this.configurationService.applicationUrls.getMapping;

        return this.http.get(url, {
            params: {
                state: req.state,
                code: req.code
            }
        });
    }

    getPublicIPAddress() {
        // const url = this.configurationService.applicationUrls.geolocationAPIUrl;

        return new Observable(observer => {
            const data = { ip : "10.0.0.90"};
            observer.next(data);
            observer.complete();
        });
    }

    forgotPasswordForEmail() {
        if (this.forgotEmail) {
            let executeFunction = () => {
                this.forgotPassword(this.forgotEmail).then(pr => {
                    pr.subscribe(res => {
                        this.toastr.simpleCustomToaster(res.message);
                        this.router.navigateByUrl('/auth/login');
                    })
                })
            };

            this.confirmationService.alert({
                message: ERROR_LOGIN_WITH_GOOGLE,
                buttons: [
                    {
                        text: 'Cancel',
                        accent: true,
                        class: 'undeploy-button',
                    },
                    {
                        text: 'Proceed',
                        accent: true,
                        class: 'new-theme-gradient-bg',
                        handler: executeFunction
                    }
                ]
            }, {
                disableClose: false,
            });
        }
    }

    async setCrmUtmFields(params: ReffererStructure) {
        const crmUtmFields = Object.assign({}, params);
        const crmUtmFieldsKeys = Object.keys(params);

        let utmFields = crmUtmFieldsArray;
        if (crmUtmFieldsKeys?.length) {
            crmUtmFields['Referrer_URL'] = params.Referrer_URL ?? window.location.href;
            crmUtmFields['referral_code'] = params.referral_code ?? '';
            crmUtmFields['IP'] = params.IP;


            if (!crmUtmFields['IP']) {
                const data: any = await this.fetchClientIP(crmUtmFields);
                crmUtmFields['IP'] = data.ip;
            }
        }

        // If any of the UTM fields are present , present UTM fields will be set and rest would be assigned as empty strings .
        // IF not , Default values of the UTM fields present in the firebase will be used .
        if (crmUtmFieldsKeys.some((key) => utmFields.includes(key))) {
            utmFields.forEach(field => {
                crmUtmFields[field] = params[field] || "";
            })
        } else {
            crmUtmFields['utm_campaign'] = this.configurationService.remoteConfigCacheObj.DEFAULT_UTM_CAMPAIGN;
            crmUtmFields['utm_source'] = document.referrer || this.configurationService.remoteConfigCacheObj.DEFAULT_REFERER;
        }

        return crmUtmFields;
    }

    fetchClientIP(crmUtmFields: ReffererStructure) {
        return this.getPublicIPAddress().toPromise();
    }

    showUpgradePlanPopup(message: string) {
        this.confirmationService.alert({
            message: message,
            buttons: [
                {
                    text: 'Cancel',
                    accent: true,
                    class: 'undeploy-button',
                },
                {
                    text: 'Upgrade',
                    accent: true,
                    class: 'new-theme-gradient-bg',
                    handler: () => { this.routeToPlans() }
                }
            ]
        }, {
            disableClose: false,
        });
    }

    routeToPlans() {
        this.router.navigate(['/trading/profile/user/plans']);
    }


    /**
     * Makes an HTTP PUT request to authenticate an admin operator login
     * @param token The authorization token to include in request headers
     */
    adminLoginOperator(token: string) {
        if (!isOperator) {
            return;
        }
        StorageHelper.operatorAdminToken = token;
        const headers = new HttpHeaders({
            'Authorization': token
        })
        this.http.put(this.configurationService.applicationUrls.adminLoginOperator, null, { headers }).subscribe();
    }

    //Multi Broker Specific

    multiBrokerEmailPhoneLogin(req: MultiBrokerEmailPhoneLoginRequest, requestToken: string | null = null) {
        let headers: HttpHeaders;
        if (requestToken) {
            headers = new HttpHeaders({
                'X-Recaptcha-Token': this.captchaToken,
                'request-token': requestToken
            })
        } else {
            headers = new HttpHeaders({
                'X-Recaptcha-Token': this.captchaToken
            })
        }
        return this.http.post(this.configurationService.applicationUrls.multiBrokerEmailPhoneLogin, req, {
            headers
        });
    }

    multiBrokerVerifyOTP(req: MultiBrokerVerifyOTPRequest, requestToken: string | null = null) {
        let headers: HttpHeaders;
        if (requestToken) {
            headers = new HttpHeaders({
                'X-Recaptcha-Token': this.captchaToken,
                'request-token': requestToken
            })
        } else {
            headers = new HttpHeaders({
                'X-Recaptcha-Token': this.captchaToken
            })
        }
        return this.http.post(this.configurationService.applicationUrls.multiBrokerVerifyOTP, req, { headers });
    }

    multiBrokerResendOTP(req, requestToken: string | null = null) {
        let headers: HttpHeaders;
        if (requestToken) {
            headers = new HttpHeaders({
                'X-Recaptcha-Token': this.captchaToken,
                'request-token': requestToken
            })
        } else {
            headers = new HttpHeaders({
                'X-Recaptcha-Token': this.captchaToken
            })
        }
        return this.http.post(this.configurationService.applicationUrls.multiBrokerResendOTP, req, {
            headers
        });
    }


    handleMultiBrokerAuthResponse(resp: MultiBrokerVerifyOTPResponse) {
        if (resp.data && resp.data.error) {
            if (resp.data.error === ERROR_CALLING_RPC) {
                this.toastr.simpleCustomToaster(ACCOUNT_INACTIVE_BROKER_MESSAGE, false);
            } else {
                this.toastr.simpleCustomToaster(resp.data.error, false);
            }
            //check if access token is avl in local storage then navigate to manage-broker else login page
            if (StorageHelper.userInfo?.access) {
                this.router.navigate(['/trading/manage-brokers']);
            } else {
                this.router.navigate(['/auth/login']);
            }
            return;
        }
        const id = uuid();
        StorageHelper.Uuid = id;

        this.userInfo = {
            access: resp.data.access_token,
            refresh: resp.data.refresh_token
        };

        this.accessTokenObj = new JWTUtilClass(this.userInfo.access, true);
        this.refreshTokenObj = new JWTUtilClass(this.userInfo.refresh, true);
        StorageHelper.userInfo = this.userInfo;
        this.syncMixpanelIdentity();

        if (resp.broker_data) {
            const brokerData = resp.broker_data;
            if (brokerData.status !== BrokerConnectionStatus.CONNECTIONSTATUS_ACTIVE) {
                this.mixpanel.track(MIXPANEL_EVENTS.LOGIN_VIA_BROKER, {
                    broker_name: resp.broker_data.broker,
                    // TODO: Ucc will be added in future via BE
                    // ucc: resp.broker_data.broker_id
                });
                this.router.navigate(['/trading/manage-brokers'], { queryParams: { brokerId: resp.broker_data.broker_id, broker: resp.broker_data.broker, status: brokerData.status } });
                return;
            }
        }
        this.router.navigate(['/trading/dashboard']);
        // window.location.replace(window.location.origin + '/trading/dashboard');
    }

    initMultiBrokerLogin(provider: string) {
        return this.http.get<any>(this.configurationService.applicationUrls.getMultiBrokerLoginInitURL(provider), {
            headers: new HttpHeaders({
                'X-Recaptcha-Token': this.captchaToken
            })
        });
    }

    /**
     * Starts multi-broker social login (redirect to provider).
     * @param requestToken - Optional broker `request-token` header (multi-broker login). Omit or pass undefined when not in broker flow.
     * @param fetchUserDetails - When true and user already has a session, still fetches the init response (e.g. profile / initial-user-details) instead of navigating away.
     */
    async initMultiBrokerSocialLogin(provider: string, requestToken?: string, fetchUserDetails: boolean = false) {
        if (StorageHelper.userInfo?.access && !fetchUserDetails) {
            this.toastr.simpleCustomToaster(LOGGED_IN_MSG);
            this.setToken();
            this.router.navigate(['/trading/dashboard']);
        } else {
            let headers: HttpHeaders;
            if (requestToken) {
                headers = new HttpHeaders({
                    'X-Recaptcha-Token': this.captchaToken,
                    'request-token': requestToken
                })
            } else {
                headers = new HttpHeaders({
                    'X-Recaptcha-Token': this.captchaToken
                });
            }
            return this.http.get<MultiBrokerSocialLoginInitResponse>(this.configurationService.applicationUrls.getMultiBrokerSocialLoginInitURL(provider), {
                headers: headers
            });
        }
    }

    async LoginCallback(provider: string, params: any, requestToken?: string) {

        let headers: HttpHeaders;
        if (requestToken) {
            headers = new HttpHeaders({
                'X-Recaptcha-Token': this.captchaToken,
                'request-token': requestToken
            })
        } else {
            headers = new HttpHeaders({
                'X-Recaptcha-Token': this.captchaToken
            })
        }

        try {
            this.eventsService.publish(CustomEvents.ShowAppLoader, true);

            const callbackResponse = await this.http.post<MultiBrokerVerifyOTPResponse>(this.configurationService.applicationUrls.getMultiBrokerSocialLoginCallback(provider), params, {
                headers: headers
            }).toPromise().catch(err => {
                throw err;
            }).finally(() => {
                this.eventsService.publish(CustomEvents.ShowAppLoader, false);
            });
            if(!callbackResponse?.data?.error) {
                if(!this.isAuthenticated ) {
                    if (provider === APPLE_VENDOR_NAME) {
                        this.mixpanel.track(MIXPANEL_EVENTS.LOGIN_SUCCESS_APPLE, {
                            user_type: callbackResponse?.broker_data?.broker || 'Guest',
                            email: callbackResponse?.data?.email || ''
                        });
                    } else if (provider === GOOGLE_VENDOR_NAME) {
                        // Fired after SDK-based Google login (GoogleAuthProvider → LoginCallback)
                        this.mixpanel.track(MIXPANEL_EVENTS.LOGIN_SUCCESS_GOOGLE, {
                            email: callbackResponse?.data?.email || '',
                            user_type: callbackResponse?.broker_data?.broker || 'Guest'
                        });
                    }
                } else  {
                    this.mixpanel.track(MIXPANEL_EVENTS.NAME_EMAIL_ADDITION_SUCCESS, {
                        name: callbackResponse?.full_name || '',
                        email: callbackResponse?.email || ''
                    });
                }
            }
            // When user is already logged in (e.g. initial-user-details dialog),
            // skip token storage / navigation — just return the response so the
            // caller can read full_name, email, etc.
            if (this.isAuthenticated) {
                return of(callbackResponse);
            }

            this.handleMultiBrokerAuthResponse(callbackResponse)
            return of(callbackResponse);

        } catch (error) {
            console.error(error);
            this.captchaToken = '';
            this.eventsService.publish(CustomEvents.ShowAppLoader, false);
            return throwError(error);
        }

    }
}
